/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// SRAM: the Badge has TWO style axes, matching Figma's component set (25:18):
// `variant` carries meaning, `appearance` carries visual weight. The upstream
// single flat list is replaced, not extended — 21 names encoding two orthogonal
// ideas cannot express "same colour, lighter weight" without a string rewrite,
// and the promotional badges below depend on content being independent of both.
//
// Each appearance is a rule about WHICH TOKEN TIER to read, not a hand-written
// colour set (see openspec design-tokens, "Status colours are tiered by treatment"):
//
//   default  -> fill tier         bg fill-*            text on-fill-* (white)
//   subtle   -> subtle-fill tier  bg fill-*-subtle     text on-fill-*-subtle
//   clear    -> content tier      no bg                text text-color-*
//
// NOTE for the fill tier: on-fill is WHITE for every chromatic variant. Do NOT
// reach for --success-foreground / --warning-foreground / --destructive-foreground
// here — those hold the SUBTLE FILL BACKGROUNDS despite their names, which is why
// they appear in the `subtle` rows below. core.css has the full map.
//
// Figma gives the badge --border-radius-none (0). No radius class appears below:
// --ui-radius is already 0 at :root, so `rounded-ui` resolves to 0 and the correct
// change was DELETING the previous `rounded-full`. Never `rounded-none` here.
const badgeVariants = cva(
  // SRAM: icon rules select on :is(svg, [data-slot='icon']) rather than svg alone,
  // following button.tsx. The Icon component renders <span class="material-symbols-sharp">,
  // which svg-scoped rules silently miss — no error, just a 24px glyph beside a
  // 14px label. `size-*` scales a lucide <svg>; `text-[16px]` scales the font glyph.
  // Both land on icon/sm 16px per Figma. The svg rule keeps its :not() escape hatch;
  // the glyph rule does not need one, since Icon's `size` prop lands inline and beats
  // every class.
  //
  // Metrics from Figma 25:18: 24px box (--size-6), 8px inline / 4px block padding
  // (--spacing-2 / --spacing-xs), 14px label at weight 450 with 3% tracking
  // (components/badge). The unitless `letterSpacing: 3` in this Figma file is
  // PERCENT, per the convention the design owner confirmed for the buttons work.
  //
  // `w-fit shrink-0` were previously bolted on per call site via a duplicated
  // BADGE_BASE_CLASSES string; they belong here.
  // The upstream base carried `border` (1px). Figma's badge draws no border on any
  // of the 17 cells, so it is dropped rather than neutralised with border-0 — the
  // repo's shape rules forbid neutralising with a utility. A call site that needs
  // one adds `border` plus a colour itself.
  "inline-flex items-center justify-center w-fit shrink-0 gap-1 whitespace-nowrap rounded-ui h-6 px-2 py-1 text-sm font-book tracking-[0.03em] leading-none [&_:is(svg,[data-slot=icon])]:pointer-events-none [&_:is(svg,[data-slot=icon])]:shrink-0 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-4 [&_[data-slot=icon]]:text-[16px] transition-[color,box-shadow] overflow-hidden focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        neutral: "",
        brand: "",
        accent: "",
        success: "",
        attention: "",
        warning: "",
        info: "",
      },
      appearance: {
        default: "",
        subtle: "",
        // No fill of its own — the page surface shows through.
        clear: "bg-transparent",
      },
    },
    // The colour system lives here rather than on `variant`, because a cell is a
    // (variant, appearance) pair and neither axis carries a colour on its own.
    compoundVariants: [
      // -- Neutral: achromatic, per the design. Not blue — `info` below is the
      //    chromatic informational variant, and the two are distinct statuses.
      {
        variant: "neutral" as const,
        appearance: "default" as const,
        // fill-neutral #141414 / on-fill-neutral white — 18.4:1
        class: "bg-primary text-primary-foreground [a&]:hover:bg-primary-hover",
      },
      {
        variant: "neutral" as const,
        appearance: "subtle" as const,
        // fill-neutral-subtle #f5f5f5 / on-fill-neutral-subtle #4a4a4a — 8.13:1
        class: "bg-muted text-input-foreground [a&]:hover:bg-muted-hover",
      },
      {
        variant: "neutral" as const,
        appearance: "clear" as const,
        // text-color-primary #000000 — 21:1
        class: "text-foreground [a&]:hover:bg-muted",
      },

      // -- Brand: a BRAND MOMENT (promotional), never a status. See core.css.
      {
        variant: "brand" as const,
        appearance: "default" as const,
        // fill-brand #e51937 / white — 4.67:1. Passes, but by 0.17; do not lighten
        // the label weight or shrink the type without re-measuring.
        class: "bg-brand text-brand-foreground [a&]:hover:bg-brand-hover",
      },
      {
        variant: "brand" as const,
        appearance: "subtle" as const,
        // DERIVED — Figma ships no Brand/Subtle cell. red-600 #c0122c on grey-50
        // #f5f5f5 = 5.73:1. Brand red-500 #e51937 would be 4.29:1 and FAIL AA at
        // this size, so the pairing steps one ramp position down. Awaiting design
        // confirmation (CRCB2B-640).
        class:
          "bg-brand-subtle text-brand-subtle-foreground [a&]:hover:bg-muted-hover",
      },
      {
        variant: "brand" as const,
        appearance: "clear" as const,
        // text-color-brand #e51937 — 4.67:1
        class: "text-brand [a&]:hover:bg-brand-subtle",
      },

      // -- Accent: the grey status. Figma's fill-accent is grey-50 with BLACK text,
      //    while fill-accent-subtle is grey-100 with #4a4a4a. Both pairings already
      //    exist as --secondary/--secondary-foreground and --accent/--accent-foreground.
      {
        variant: "accent" as const,
        appearance: "default" as const,
        // fill-accent #f5f5f5 / on-fill-accent #000000 — 19.3:1
        class:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary-hover",
      },
      {
        variant: "accent" as const,
        appearance: "subtle" as const,
        // fill-accent-subtle #eaeaea / on-fill-accent-subtle #4a4a4a — 7.36:1
        class: "bg-accent text-accent-foreground [a&]:hover:bg-accent-hover",
      },
      {
        variant: "accent" as const,
        appearance: "clear" as const,
        // text-color-accent #4a4a4a — 8.86:1
        class: "text-accent-foreground [a&]:hover:bg-accent",
      },

      // -- Success
      {
        variant: "success" as const,
        appearance: "default" as const,
        // fill-success #0e6c05 / white — 6.63:1
        class: "bg-success text-white [a&]:hover:bg-success-hover",
      },
      {
        variant: "success" as const,
        appearance: "subtle" as const,
        // fill-success-subtle #d5f5d0 / on-fill-success-subtle #1b5915 — 7.16:1
        class: "bg-success-foreground text-success-subtle-foreground",
      },
      {
        variant: "success" as const,
        appearance: "clear" as const,
        // text-color-success #007f00 — 5.20:1
        class: "text-success-text [a&]:hover:bg-success-foreground",
      },

      // -- Attention: the error status. Named `attention` after the SRAM ramp, not
      //    `destructive` or `error`, matching buttons/toast/alert (CRCB2B-643/641/644).
      {
        variant: "attention" as const,
        appearance: "default" as const,
        // fill-attention #a70e00 / white — 7.76:1
        class: "bg-destructive text-white [a&]:hover:bg-destructive-hover",
      },
      {
        variant: "attention" as const,
        appearance: "subtle" as const,
        // fill-attention-subtle #ffe1d9 / on-fill-attention-subtle #783030 — 7.52:1
        class: "bg-destructive-foreground text-destructive-subtle-foreground",
      },
      {
        variant: "attention" as const,
        appearance: "clear" as const,
        // text-color-attention #c21100 — 6.22:1
        class: "text-error [a&]:hover:bg-destructive-foreground",
      },

      // -- Warning
      {
        variant: "warning" as const,
        appearance: "default" as const,
        // KNOWN AA FAILURE (CRCB2B-627): fill-warning #b06b00 with white measures
        // 4.24:1 against the 4.5:1 obligation this 14px regular label carries.
        // Built as designed — the third consumer of one base-ramp defect, alongside
        // the button and the alert banner. The fix is darkening yellow-500 in
        // base.css, which closes every consumer at once; a local override here would
        // mask it and need reverting. Tracked in styleguide-finalization task 6.1.
        class: "bg-warning text-white [a&]:hover:bg-warning-hover",
      },
      {
        variant: "warning" as const,
        appearance: "subtle" as const,
        // fill-warning-subtle #ffe6c7 / on-fill-warning-subtle #674103 — 7.45:1
        class: "bg-warning-foreground text-warning-subtle-foreground",
      },
      {
        variant: "warning" as const,
        appearance: "clear" as const,
        // text-color-warning #935900 — 5.73:1. Note the TEXT value clears the bar
        // the FILL above misses.
        class: "text-warning-text [a&]:hover:bg-warning-foreground",
      },

      // -- Info: NOT a Figma variant. A deliberate addition for parity across the
      //    status colours, exactly as the `info` button is (CRCB2B-643). Draws on
      //    the interim blue ramp; swap when SRAM delivers an official blue.
      {
        variant: "info" as const,
        appearance: "default" as const,
        // --info #12467d / white — 9.55:1
        class: "bg-info text-white [a&]:hover:bg-info-hover",
      },
      {
        variant: "info" as const,
        appearance: "subtle" as const,
        // --info-foreground #d4e5f7 (a subtle FILL, despite the name) / #12467d — 7.44:1
        class: "bg-info-foreground text-info-subtle-foreground",
      },
      {
        variant: "info" as const,
        appearance: "clear" as const,
        // BORROWED from the fill tier: the blue ramp has no content-tier step, so
        // this reuses --info per the token spec's borrow-rather-than-invent rule.
        // 9.55:1.
        class: "text-info [a&]:hover:bg-info-foreground",
      },
    ],
    defaultVariants: {
      variant: "neutral",
      appearance: "default",
    },
  }
)

function Badge({
  className,
  variant,
  appearance,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, appearance }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
